'use strict';

/**
 * NACRE Wave 3.2.1 — Browser shim for Node's `crypto` builtin
 *
 * Aliased into the vault browser bundle by esbuild's `alias` config:
 *   alias: { crypto: './scripts/esbuild-crypto-shim.js' }
 *
 * This file IS NEVER LOADED IN NODE — only when bundled for the browser.
 * It re-implements the SUBSET of Node's `crypto` API that the vault
 * client modules actually use, on top of @noble/hashes and @noble/ciphers
 * (pure JS) plus Web Crypto's globalThis.crypto.getRandomValues for randomness.
 *
 * Implements:
 *   - randomBytes(n) → Uint8Array (browser-safe via Web Crypto)
 *   - createHash('sha256' | 'sha512')
 *       .update(data) chained
 *       .digest('hex' | undefined)
 *   - createCipheriv('aes-256-gcm', key, iv)
 *       .update(data) → empty Buffer (deferred)
 *       .final() → Buffer of full ciphertext minus tag
 *       .getAuthTag() → 16-byte Buffer
 *   - createDecipheriv('aes-256-gcm', key, iv)
 *       .setAuthTag(tag) → must be called before final
 *       .update(data) → empty Buffer (deferred)
 *       .final() → Buffer of decrypted plaintext
 *
 * Anything outside this surface throws — the shim is intentionally
 * minimal so we don't accidentally support Node-only features that
 * would silently misbehave in the browser.
 */

const { sha256, sha512 } = require('@noble/hashes/sha2.js');
const { hkdf } = require('@noble/hashes/hkdf.js');
const { gcm } = require('@noble/ciphers/aes.js');

// B2/B4 JIT mint — jit-grant.js destructures randomUUID from node:crypto.
// CSPRNG-backed UUIDv4 (RFC 4122): prefer the platform's native
// crypto.randomUUID (secure contexts); otherwise assemble the UUID from
// getRandomValues bytes. Both paths draw from the Web Crypto CSPRNG —
// never anything weaker.
function randomUUID() {
  const c = typeof globalThis !== 'undefined' && globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = randomBytes(16);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // RFC 4122 variant
  const hex = bytesToHex(b);
  return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16)
    + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
}

// Universal randomBytes: Web Crypto in browsers (and modern Node).
function randomBytes(length) {
  if (typeof length !== 'number' || length < 0) {
    throw new TypeError('randomBytes length must be a non-negative number');
  }
  const out = new Uint8Array(length);
  if (typeof globalThis !== 'undefined' && globalThis.crypto && typeof globalThis.crypto.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(out);
  } else {
    throw new Error('No CSPRNG available — globalThis.crypto.getRandomValues missing');
  }
  // Return a Buffer-like object (Buffer is polyfilled by esbuild's inject)
  return Buffer.from(out);
}

function toBytes(data, encoding) {
  if (data == null) return new Uint8Array(0);
  if (data instanceof Uint8Array) return data;
  if (typeof data === 'string') {
    if (encoding === 'hex') return Buffer.from(data, 'hex');
    if (encoding === 'base64') return Buffer.from(data, 'base64');
    return new TextEncoder().encode(data);
  }
  if (Buffer.isBuffer && Buffer.isBuffer(data)) return data;
  throw new TypeError('Unsupported data type for toBytes');
}

function bytesToHex(bytes) {
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

function createHash(algorithm) {
  let chunks = [];
  let totalLen = 0;
  const algo = String(algorithm).toLowerCase();
  if (algo !== 'sha256' && algo !== 'sha-256' && algo !== 'sha512' && algo !== 'sha-512') {
    throw new Error('Unsupported hash algorithm: ' + algorithm);
  }
  const hashFn = (algo === 'sha256' || algo === 'sha-256') ? sha256 : sha512;

  function combineChunks() {
    if (chunks.length === 1) return chunks[0];
    const out = new Uint8Array(totalLen);
    let offset = 0;
    for (const c of chunks) {
      out.set(c, offset);
      offset += c.length;
    }
    return out;
  }

  return {
    update(data, encoding) {
      const bytes = toBytes(data, encoding);
      chunks.push(bytes);
      totalLen += bytes.length;
      return this;
    },
    digest(encoding) {
      const combined = combineChunks();
      const hash = hashFn(combined);
      if (encoding === 'hex') return bytesToHex(hash);
      // Return Buffer for compatibility with Node's default
      return Buffer.from(hash);
    },
  };
}

// HKDF shim (T-cert sealed SAVE) — master-key-encryption.js derives per-record
// DEKs via crypto.hkdfSync('sha512', ...). Byte-parity with Node is pinned by
// __tests__/media-editor/vault-seal-agent-tcert.test.js (differential test).
// Node returns an ArrayBuffer; every caller wraps Buffer.from(...), which
// accepts this Uint8Array identically.
function hkdfSync(digest, ikm, salt, info, keylen) {
  const algo = String(digest).toLowerCase();
  if (algo !== 'sha256' && algo !== 'sha-256' && algo !== 'sha512' && algo !== 'sha-512') {
    throw new Error('Unsupported hkdfSync digest: ' + digest);
  }
  const hashFn = (algo === 'sha256' || algo === 'sha-256') ? sha256 : sha512;
  return hkdf(hashFn, toBytes(ikm), toBytes(salt), toBytes(info), keylen);
}

// AES-256-GCM shim. The Node API splits encryption across update() +
// final() + getAuthTag(); @noble/ciphers does it all at once. We defer
// the encryption until final() and stash the tag.
function createCipheriv(algorithm, key, iv) {
  if (algorithm !== 'aes-256-gcm') {
    throw new Error('Only aes-256-gcm is supported by the shim (got ' + algorithm + ')');
  }
  if (!(key instanceof Uint8Array) && !Buffer.isBuffer(key)) {
    throw new TypeError('key must be Uint8Array or Buffer');
  }
  if (!(iv instanceof Uint8Array) && !Buffer.isBuffer(iv)) {
    throw new TypeError('iv must be Uint8Array or Buffer');
  }
  const keyBytes = key instanceof Uint8Array ? key : new Uint8Array(key);
  const ivBytes = iv instanceof Uint8Array ? iv : new Uint8Array(iv);
  const cipher = gcm(keyBytes, ivBytes);

  let pendingChunks = [];
  let pendingLen = 0;
  let resultCt = null;
  let resultTag = null;

  function combine() {
    const out = new Uint8Array(pendingLen);
    let offset = 0;
    for (const c of pendingChunks) {
      out.set(c, offset);
      offset += c.length;
    }
    return out;
  }

  return {
    update(data, inputEncoding /* unused */, outputEncoding /* unused */) {
      const bytes = toBytes(data, inputEncoding);
      pendingChunks.push(bytes);
      pendingLen += bytes.length;
      // Node returns the partial ct; we defer.
      return Buffer.from([]);
    },
    final() {
      const plaintext = combine();
      const ctWithTag = cipher.encrypt(plaintext);
      // gcm.encrypt() returns ciphertext || tag concatenated. Tag is 16 bytes.
      const tagStart = ctWithTag.length - 16;
      resultCt = ctWithTag.slice(0, tagStart);
      resultTag = ctWithTag.slice(tagStart);
      return Buffer.from(resultCt);
    },
    getAuthTag() {
      if (resultTag === null) {
        throw new Error('getAuthTag() called before final()');
      }
      return Buffer.from(resultTag);
    },
  };
}

function createDecipheriv(algorithm, key, iv) {
  if (algorithm !== 'aes-256-gcm') {
    throw new Error('Only aes-256-gcm is supported by the shim (got ' + algorithm + ')');
  }
  const keyBytes = key instanceof Uint8Array ? key : new Uint8Array(key);
  const ivBytes = iv instanceof Uint8Array ? iv : new Uint8Array(iv);
  const cipher = gcm(keyBytes, ivBytes);

  let pendingChunks = [];
  let pendingLen = 0;
  let pendingTag = null;
  let resultPt = null;

  function combine() {
    const out = new Uint8Array(pendingLen);
    let offset = 0;
    for (const c of pendingChunks) {
      out.set(c, offset);
      offset += c.length;
    }
    return out;
  }

  return {
    setAuthTag(tag) {
      pendingTag = tag instanceof Uint8Array ? tag : new Uint8Array(tag);
      return this;
    },
    update(data) {
      const bytes = toBytes(data);
      pendingChunks.push(bytes);
      pendingLen += bytes.length;
      return Buffer.from([]);
    },
    final() {
      if (pendingTag === null) {
        throw new Error('setAuthTag() must be called before final()');
      }
      const ct = combine();
      // @noble/ciphers expects ct || tag concatenated for decrypt
      const ctWithTag = new Uint8Array(ct.length + pendingTag.length);
      ctWithTag.set(ct, 0);
      ctWithTag.set(pendingTag, ct.length);
      // decrypt() throws if the tag doesn't match — same as Node's behavior
      resultPt = cipher.decrypt(ctWithTag);
      return Buffer.from(resultPt);
    },
  };
}

module.exports = {
  randomBytes,
  randomUUID,
  createHash,
  createCipheriv,
  createDecipheriv,
  hkdfSync,
};
